import React from 'react';
import { Box, Chip, Typography, Button, Alert } from '@mui/material';
import {
    Security as SecurityIcon,
    Warning as WarningIcon,
    CheckCircle as CheckCircleIcon,
    Cancel as CancelIcon,
} from '@mui/icons-material';
import { useSecureKey, useSecureKeyActions } from '../../stores/secureKeyStore';

interface SecureKeyStatusProps {
    onReAuthenticate?: () => void;
    showDetails?: boolean;
    compact?: boolean;
}

export const SecureKeyStatus: React.FC<SecureKeyStatusProps> = ({
    onReAuthenticate,
    showDetails = true,
    compact = false,
}) => {
    const { hasActiveKey, isExpiringSoon } = useSecureKey();
    const { renew, revoke } = useSecureKeyActions();

    const handleRenew = async () => {
        try {
            await renew();
        } catch (error) {
            console.error('Failed to renew secure key:', error);
            // If renewal fails, prompt for re-authentication
            onReAuthenticate?.();
        }
    };

    const handleRevoke = async () => {
        try {
            await revoke();
        } catch (error) {
            console.error('Failed to revoke secure key:', error);
        }
    };

    if (!hasActiveKey) {
        return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Chip
                    icon={<CancelIcon />}
                    label="Ingen åtkomst till patientdata"
                    color="default"
                    variant="outlined"
                    size={compact ? 'small' : 'medium'}
                />
                {showDetails && !compact && (
                    <Typography variant="body2" color="text.secondary">
                        Autentisera dig för att få åtkomst till patientdata
                    </Typography>
                )}
            </Box>
        );
    }

    const getStatusColor = () => {
        if (isExpiringSoon) return 'warning';
        return 'success';
    };

    const getStatusIcon = () => {
        if (isExpiringSoon) return <WarningIcon />;
        return <CheckCircleIcon />;
    };

    const getStatusLabel = () => {
        if (isExpiringSoon) return 'Går ut snart';
        return 'Aktiv';
    };

    if (compact) {
        return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Chip
                    icon={getStatusIcon()}
                    label={`Åtkomst till patientdata: ${getStatusLabel()}`}
                    color={getStatusColor()}
                    size="small"
                />
                {isExpiringSoon && (
                    <Button
                        size="small"
                        variant="outlined"
                        onClick={handleRenew}
                        startIcon={<SecurityIcon />}
                    >
                        Förnya
                    </Button>
                )}
                <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    onClick={handleRevoke}
                >
                    Återkalla
                </Button>
            </Box>
        );
    }

    return (
        <Box>
            {isExpiringSoon && (
                <Alert
                    severity="warning"
                    sx={{ mb: 2 }}
                    action={
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button
                                color="inherit"
                                size="small"
                                onClick={handleRenew}
                                startIcon={<SecurityIcon />}
                            >
                                Förnya åtkomst
                            </Button>
                            <Button
                                color="inherit"
                                size="small"
                                onClick={onReAuthenticate}
                            >
                                Autentisera igen
                            </Button>
                        </Box>
                    }
                >
                    Din åtkomst till patientdata går ut snart. Förnya för att
                    fortsätta få åtkomst till patientinformation.
                </Alert>
            )}

            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    mb: showDetails ? 2 : 0,
                }}
            >
                <Chip
                    icon={getStatusIcon()}
                    label={`Åtkomst till patientdata: ${getStatusLabel()}`}
                    color={getStatusColor()}
                />

                <Box sx={{ display: 'flex', gap: 1 }}>
                    {isExpiringSoon && (
                        <Button
                            size="small"
                            variant="outlined"
                            onClick={handleRenew}
                            startIcon={<SecurityIcon />}
                        >
                            Förnya
                        </Button>
                    )}
                    <Button
                        size="small"
                        variant="outlined"
                        color="error"
                        onClick={handleRevoke}
                    >
                        Återkalla
                    </Button>
                </Box>
            </Box>

            {showDetails && (
                <Box>
                    <Typography variant="body2" color="text.secondary">
                        Säker nyckel ger åtkomst till patientdata i
                        transaktionsrapporter.
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        Åtkomsten går automatiskt ut efter 15 minuter av
                        inaktivitet av säkerhetsskäl.
                    </Typography>
                </Box>
            )}
        </Box>
    );
};

export default SecureKeyStatus;
